import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTheme } from 'next-themes';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { LoginPage } from '@/components/auth/LoginPage';
import {
  DynamicBackground,
  type BackgroundConfig,
} from '@/components/layout/DynamicBackground';
import { resolveLandingText, type LandingConfig } from '@/lib/landingConfig';

/**
 * DeviceLandingPage — device-specific onboarding/login surface reached via
 * the QR code generated on the device page (api-devices → generate-qr).
 *
 * The page calls `get_device_landing_context`, a SECURITY DEFINER RPC that
 * merges the device-level overrides stored on `device_pwa_config` with the
 * organization defaults on `organizations`. Device values win; untouched
 * fields fall through to the organization, so a device inherits the org
 * Mobilapplikation design until the admin explicitly marks it as "specifik".
 *
 * Reuses the same DynamicBackground + LoginPage components that power the
 * org slug landing so device pages stay visually consistent.
 */
interface DeviceLandingContext {
  device_id: string;
  device_code: string;
  device_name: string | null;
  organization_id: string;
  organization_name: string;
  organization_slug: string;
  logo_url: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  primary_color: string | null;
  background_config: BackgroundConfig | null;
  auth_config: {
    bankid_enabled?: boolean;
    allowed_methods?: string[];
    primary_method?: string;
    method_configs?: Record<string, { enabled?: boolean; [key: string]: any }>;
    access?: { allowed_methods?: string[]; primary_method?: string };
  } | null;
  landing_config: LandingConfig | null;
  has_device_overrides: boolean;
}

const ACCESS_METHODS = ['email', 'phone', 'bankid', 'invite'] as const;

export default function DeviceLandingPage() {
  const { deviceCode = '' } = useParams<{ deviceCode: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { resolvedTheme } = useTheme();

  const [context, setContext] = useState<DeviceLandingContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Fetch merged device + organization landing context via the public RPC.
  useEffect(() => {
    if (!deviceCode) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const fetchContext = async () => {
      const { data, error } = await supabase.rpc('get_device_landing_context', {
        p_device_code: deviceCode,
      });

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const ctx = data as DeviceLandingContext;
      setContext(ctx);

      // Persist so post-logout returns here instead of the root.
      sessionStorage.setItem('last_slug_path', `/device/${deviceCode}`);
      sessionStorage.setItem(
        'org_context',
        JSON.stringify({
          org_slug: ctx.organization_slug,
          org_id: ctx.organization_id,
          org_name: ctx.organization_name,
          device_id: ctx.device_id,
          device_code: ctx.device_code,
        }),
      );

      setLoading(false);
    };

    fetchContext();
  }, [deviceCode]);

  // Detect open access mode: all methods explicitly disabled via method_configs
  const isOpenAccess = (() => {
    const mc = context?.auth_config?.method_configs;
    if (!mc) return false;
    const methods = ['email', 'phone', 'bankid', 'invite'] as const;
    return methods.every(m => mc[m]?.enabled === false);
  })();

  // Auto-redirect authenticated users, or open access mode (no auth required)
  useEffect(() => {
    if (!authLoading && context) {
      if (user || isOpenAccess) {
        navigate('/keys');
      }
    }
  }, [user, authLoading, context, isOpenAccess, navigate]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (notFound || !context) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-6">
        <h1 className="text-2xl font-bold mb-2">Enheten hittades inte</h1>
        <p className="text-muted-foreground">
          Kontrollera att QR-koden är korrekt
        </p>
      </main>
    );
  }

  // Resolve logo with the same theme-aware fallback order as OrgLandingPage.
  const logoLightUrl = context.logo_light_url ?? undefined;
  const logoDarkUrl = context.logo_dark_url ?? undefined;
  const orgLogoUrl =
    resolvedTheme === 'dark'
      ? logoDarkUrl || logoLightUrl || context.logo_url || undefined
      : logoLightUrl || logoDarkUrl || context.logo_url || undefined;

  // Derive allowed login methods using the same precedence as OrgLandingPage:
  //   method_configs → auth_config.access → auth_config.allowed_methods → legacy toggle.
  const methodConfigs = context.auth_config?.method_configs;
  const accessConfig = context.auth_config?.access;
  const effectiveMethods = methodConfigs
    ? ACCESS_METHODS.filter((m) => methodConfigs[m]?.enabled !== false)
    : accessConfig?.allowed_methods
      ?? context.auth_config?.allowed_methods
      ?? (context.auth_config?.bankid_enabled === false
        ? ACCESS_METHODS.filter((m) => m !== 'bankid')
        : undefined);

  const primaryMethod =
    accessConfig?.primary_method ?? context.auth_config?.primary_method;

  const backgroundConfig = context.background_config ?? null;
  const primaryColor = context.primary_color ?? undefined;

  // Merged landing text/visibility (device override ∪ org default from the RPC).
  const landingText = resolveLandingText(context.landing_config);

  // Solid base hides the global AuroraBackground when the device has a
  // custom backdrop, so low-opacity gradients don't leak the ambient aurora.
  const baseLayer = backgroundConfig ? (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none bg-background"
      style={{ zIndex: -1 }}
    />
  ) : null;

  return (
    <>
      {baseLayer}
      <DynamicBackground config={backgroundConfig} />
      <LoginPage
        orgName={context.device_name || context.organization_name}
        orgSlug={context.organization_slug}
        orgLogoUrl={orgLogoUrl}
        allowedMethods={effectiveMethods}
        primaryMethod={primaryMethod}
        primaryColor={primaryColor}
        hasCustomBackground={!!backgroundConfig}
        headline={landingText.headline}
        subheadline={landingText.subheadline}
        showLogo={landingText.showLogo}
        showHeadline={landingText.showHeadline}
        showSubheadline={landingText.showSubheadline}
      />
    </>
  );
}
